import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  getAnnotationToolAdminProjectsPath,
  useAppDispatch,
  useAppSelector,
} from '@verantos-dev/core-annotation-tool';

import { adminProjectsApiSlice } from '../../admin-projects/api/adminProjectsApiSlice';
import { ADMIN_PROJECTS_TAG } from '../../admin-projects/constants';
import {
  useCreateAnnotationProjectMutation,
  useGetCreateProjectStatusQuery,
} from '../api/createProjectApiSlice';
import { type WizardStepKey } from '../constants';
import {
  useCreateProjectWizardSteps,
  useIsAgentStepEnabled,
} from '../hooks/useCreateProjectWizardSteps';
import { clearAllWizardStates, setActiveStep } from '../state/createProjectSlice';
import {
  selectActiveStep,
  selectAddedConcepts,
  selectAnnotationAgent,
  selectConditionArea,
  selectDefaultAllocationMode,
  selectHealthSystem,
  selectHumanSharePercentages,
  selectProjectName,
  selectSelectedAnnotators,
  selectSelectedSupervisors,
  selectWorkflowId,
} from '../state/selector';
import { buildCreateProjectRequest } from '../utils/buildCreateProjectRequest';

interface UseWizardFooterResult {
  isFirstStep: boolean;
  goToPreviousStep: () => void;
  handleCreateAnnotationProject: () => void;
  goToNextStep: () => void;
  nextStep: () => WizardStepKey | null;
  isLastStep: boolean;
  isCreateAnnotationProjectLoading: boolean;
  createAnnotationProjectError: string | null;
  showCreateProjSuccessModal: boolean;
  setShowCreateProjSuccessModal: (showCreateProjSuccessModal: boolean) => void;
  projName: string;
}
interface UseWizardFooterProps {
  setRequiredFieldsFilled: (requiredFieldsFilled: boolean) => void;
}
export const useWizardFooter = ({
  setRequiredFieldsFilled,
}: UseWizardFooterProps): UseWizardFooterResult => {
  const dispatch = useAppDispatch();
  const activeStep = useAppSelector(selectActiveStep);
  const steps = useCreateProjectWizardSteps();
  const nextStep = () => {
    const nextStepIdx = activeStep + 1;
    if (nextStepIdx < steps.length) {
      return steps[nextStepIdx];
    }
    return null;
  };

  const isLastStep = activeStep === steps.length - 1;
  const isFirstStep = activeStep === 0;
  const goToNextStep = () => {
    const nextStepIdx = activeStep + 1;
    if (!isLastStep) {
      dispatch(setActiveStep(nextStepIdx));
      setRequiredFieldsFilled(false);
    }
  };
  const goToPreviousStep = () => {
    const previousStepIdx = activeStep - 1;
    if (!isFirstStep) {
      dispatch(setActiveStep(previousStepIdx));
    }
  };

  const [createAnnotationProject, { isLoading: isMutationLoading }] =
    useCreateAnnotationProjectMutation();
  const [isPolling, setIsPolling] = useState(false);
  const conditionArea = useAppSelector(selectConditionArea);
  const healthSystem = useAppSelector(selectHealthSystem);
  const projName = useAppSelector(selectProjectName);
  const addedConcepts = useAppSelector(selectAddedConcepts);
  const selectedAnnotators = useAppSelector(selectSelectedAnnotators);
  const selectedSupervisors = useAppSelector(selectSelectedSupervisors);
  const defaultAllocationMode = useAppSelector(selectDefaultAllocationMode);
  // TODO: cleanup with cp_annotation_createproject_agent ff
  const isAgentStepEnabled = useIsAgentStepEnabled();
  const annotationAgent = useAppSelector(selectAnnotationAgent);
  const humanSharePercentages = useAppSelector(selectHumanSharePercentages);
  const [createAnnotationProjectError, setCreateAnnotationProjectError] = useState<string | null>(
    null
  );
  const workflowId = useAppSelector(selectWorkflowId);
  const [showCreateProjSuccessModal, setShowCreateProjSuccessModal] = useState<boolean>(false);

  const { data: projectStatusData, error: projectStatusError } = useGetCreateProjectStatusQuery(
    { workflowId: workflowId ?? '' },
    { skip: !isPolling || !workflowId, pollingInterval: 3000 }
  );

  useEffect(() => {
    if (!isPolling) return;
    if (projectStatusData?.httpStatus === 200) {
      setIsPolling(false);
      setCreateAnnotationProjectError(null);
      dispatch(adminProjectsApiSlice.util.invalidateTags([ADMIN_PROJECTS_TAG]));
      setShowCreateProjSuccessModal(true);
    }
  }, [projectStatusData, isPolling, dispatch]);

  useEffect(() => {
    if (!isPolling || !projectStatusError) return;
    setIsPolling(false);
    const errData = (projectStatusError as { data?: { detail?: string; message?: string } })?.data;
    const detail = errData?.detail ?? errData?.message ?? 'An unexpected error occurred.';
    setCreateAnnotationProjectError('Failed to create annotation project: ' + detail);
  }, [projectStatusError, isPolling]);

  const handleCreateAnnotationProject = async () => {
    try {
      await createAnnotationProject(
        buildCreateProjectRequest({
          projectName: projName,
          conditionArea,
          healthSystem,
          addedConcepts,
          selectedAnnotators,
          selectedSupervisors,
          defaultAllocationMode,
          workflowId,
          isAgentStepEnabled,
          annotationAgent,
          humanSharePercentages,
        })
      ).unwrap();
      setCreateAnnotationProjectError(null);
      setIsPolling(true);
    } catch (error) {
      console.error('Failed to create annotation project', error);
      const errData = (error as { data?: { detail?: string; message?: string } })?.data;
      const detail = errData?.detail ?? errData?.message ?? 'An unexpected error occurred.';
      setCreateAnnotationProjectError('Failed to create annotation project: ' + detail);
    }
  };

  return {
    isFirstStep,
    goToPreviousStep,
    handleCreateAnnotationProject,
    goToNextStep,
    nextStep,
    isLastStep,
    isCreateAnnotationProjectLoading: isMutationLoading || isPolling,
    createAnnotationProjectError,
    showCreateProjSuccessModal,
    setShowCreateProjSuccessModal,
    projName,
  };
};
